import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import PublicFooter from '../components/public/PublicFooter';

const NAV = [
  { to: '/', label: 'Home', hash: false, end: true },
  { to: '/#why-kr', label: 'Why KR', hash: true },
  { to: '/#agency-hub', label: 'KR Agency Hub', hash: true },
  { to: '/#training', label: 'Training & Resources', hash: true },
  { to: '/events', label: 'Events', hash: false },
];

export default function PublicLayout() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.hash]);

  const onHome = location.pathname === '/';
  const darkHeader = onHome && !scrolled && !mobileOpen;
  const logoSrc = darkHeader ? '/brand/kr-logo-dark.webp' : '/brand/kr-logo.webp';
  const linkColor = darkHeader ? 'text-slate-200 hover:text-white' : 'text-slate-700 hover:text-[#0f2a5e]';

  return (
    <div className="public-site min-h-screen bg-white text-slate-900 antialiased">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-slate-900 focus:px-3 focus:py-2 focus:rounded focus:ring-2 focus:ring-amber-500">
        Skip to main content
      </a>

      <header
        data-testid="public-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          darkHeader ? 'bg-transparent' : 'bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 lg:h-24 flex items-center gap-4">
          <Link to="/" className="flex items-center min-w-0 shrink-0" data-testid="public-header-logo" aria-label="KR Enterprise Group — Home">
            <img
              src={logoSrc}
              alt="KR Enterprise Group"
              className="h-14 sm:h-16 lg:h-[72px] w-auto object-contain transition-all duration-300"
              width="1548"
              height="454"
              decoding="async"
              fetchPriority="high"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-auto" aria-label="Main">
            {NAV.map((item) =>
              item.hash ? (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`public-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${linkColor}`}
                >
                  {item.label}
                </Link>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`public-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive ? (darkHeader ? 'text-white' : 'text-[#0f2a5e]') : linkColor
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
            <Link
              to="/login"
              data-testid="public-nav-agent-login"
              className={`ml-2 px-3.5 py-2 text-sm font-semibold rounded-full border transition-colors ${
                darkHeader ? 'border-white/25 text-white hover:bg-white/10' : 'border-slate-300 text-[#0f2a5e] hover:border-[#0f2a5e]'
              }`}
            >
              Agent Login
            </Link>
            <Link
              to="/apply"
              data-testid="public-nav-start-onboarding"
              className={`ml-2 px-3.5 py-2 text-sm font-semibold rounded-full border transition-colors ${
                darkHeader ? 'border-white/25 text-white hover:bg-white/10' : 'border-slate-300 text-[#0f2a5e] hover:border-[#0f2a5e]'
              }`}
            >
              Start Onboarding
            </Link>
            <Link
              to="/become-an-agent"
              data-testid="public-nav-become-an-agent"
              className="ml-2 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#b8862d] text-white text-sm font-semibold hover:bg-[#a67520] hover:-translate-y-0.5 shadow-sm transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            >
              Become an Agent <ArrowRight size={15} />
            </Link>
          </nav>

          <button
            type="button"
            className={`lg:hidden ml-auto p-2 rounded-md transition-colors ${darkHeader ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'}`}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            data-testid="public-mobile-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg" data-testid="public-mobile-menu">
            <nav className="px-4 py-4 flex flex-col gap-1" aria-label="Mobile">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="px-3 py-2.5 rounded-md text-base font-medium text-slate-700 hover:bg-slate-50 hover:text-[#0f2a5e]"
                >
                  {item.label}
                </Link>
              ))}
              <Link to="/login" className="px-3 py-2.5 rounded-md text-base font-medium text-[#0f2a5e]">Agent Login</Link>
              <Link
                to="/apply"
                data-testid="public-mobile-start-onboarding"
                className="mt-2 inline-flex justify-center items-center gap-1.5 px-4 py-3 rounded-full border border-slate-300 text-[#0f2a5e] text-base font-semibold"
              >
                Start Onboarding
              </Link>
              <Link
                to="/become-an-agent"
                className="mt-2 inline-flex justify-center items-center gap-1.5 px-4 py-3 rounded-full bg-[#b8862d] text-white text-base font-semibold"
              >
                Become an Agent <ArrowRight size={16} />
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main id="main-content" className={onHome ? '' : 'pt-20 lg:pt-24'}>
        <Outlet />
      </main>

      <PublicFooter />
    </div>
  );
}
